import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Users, Megaphone, Wallet, CheckCircle2, ArrowRight, Zap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Page, Spinner } from "@/components/shared";

export default function MasqHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    api.get("/me/context").then(({ data }) => {
      setCtx(data);
      localStorage.setItem("cs_cache_context", JSON.stringify(data));
    }).catch(() => {
      const cached = localStorage.getItem("cs_cache_context");
      if (cached) setCtx(JSON.parse(cached));
    });
  }, []);

  if (!ctx) return <div className="px-6 pt-10"><Spinner /></div>;

  const first = (user?.name || "there").split(" ")[0];
  const passportDone = user?.passport?.completed;
  const roadDay = ctx.road_day_active;

  const cards = [
    { icon: ShieldCheck, label: "Safety Passport", to: "/app/passport", note: passportDone ? "Complete" : "Finish setup", done: passportDone },
    { icon: Users, label: "My Crew", to: "/app/crew", note: ctx.crew?.name || "Join a crew" },
    { icon: Megaphone, label: "Band Updates", to: "/app/updates", note: "Latest news" },
    { icon: Wallet, label: "Safety Wallet", to: "/app/wallet", note: "Your benefits" },
  ];

  return (
    <Page className="px-5 pt-6">
      <div className="flex items-center justify-between">
        <Logo size="sm" />
      </div>

      <p className="mt-6 text-base" style={{ color: "var(--cs-fg2)" }}>Good day,</p>
      <h1 className="font-head font-extrabold text-3xl -mt-1">{first} 👋</h1>

      <div className="cs-card mt-4 p-4 flex items-center justify-between" style={{ borderColor: passportDone ? "var(--cs-green)" : "var(--cs-amber)" }}>
        <div className="flex items-center gap-3">
          <CheckCircle2 size={26} style={{ color: passportDone ? "var(--cs-green)" : "var(--cs-amber)" }} />
          <div>
            <p className="font-head font-bold text-lg leading-none">{passportDone ? "CARNIVAL READY" : "ALMOST READY"}</p>
            <p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>{ctx.band?.name || "No band"}</p>
          </div>
        </div>
        {!passportDone && (
          <button data-testid="home-finish-passport" onClick={() => nav("/app/passport")}
            className="cs-tap rounded-full px-4 py-2 text-xs font-bold" style={{ background: "var(--cs-amber)", color: "#000" }}>
            Finish
          </button>
        )}
      </div>

      {roadDay && (
        <button data-testid="home-enter-road-day" onClick={() => nav("/app/road-day")}
          className="cs-tap w-full magenta-grad magenta-glow rounded-2xl mt-4 p-5 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <Zap size={26} />
            <div className="text-left">
              <p className="font-head font-extrabold text-xl leading-none">ENTER ROAD DAY MODE</p>
              <p className="text-xs mt-1 opacity-90">Carnival is live right now</p>
            </div>
          </div>
          <ArrowRight size={22} />
        </button>
      )}

      <div className="grid grid-cols-2 gap-3 mt-4">
        {cards.map((c) => (
          <button key={c.label} data-testid={`home-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => nav(c.to)}
            className="cs-card cs-tap p-4 flex flex-col gap-3 text-left" style={{ minHeight: 120 }}>
            <c.icon size={24} style={{ color: c.done ? "var(--cs-green)" : "var(--cs-magenta)" }} />
            <div>
              <p className="font-bold text-base leading-tight">{c.label}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--cs-fg2)" }}>{c.note}</p>
            </div>
          </button>
        ))}
      </div>

      <button data-testid="home-help-button" onClick={() => nav("/app/help")}
        className="cs-tap w-full mt-4 rounded-2xl flex items-center justify-center gap-2 font-head font-bold"
        style={{ height: 54, background: "var(--cs-card)", border: "1px solid var(--cs-border)", color: "#fff" }}>
        I Need Help
      </button>
    </Page>
  );
}
